/* eslint-disable */

import styles from '../less/BaseLayout.module.less'
import {
  Button,
  Col,
  Collapse,
  ConfigProvider,
  Divider,
  Form,
  Input,
  InputNumber,
  Layout,
  Row,
  Select,
  Space,
  theme,
  Typography,
} from 'antd'
import { useFirestore, useFunctions } from 'reactfire'
import { collection, doc } from 'firebase/firestore'
import { api } from '../lib/api'
import useDocumentTitle from '../lib/useDocumentTitle'
import logo from '../resources/img/logo.svg'
import { formGutter, gutter } from '../lib/layout'
import { useEffect, useMemo, useState } from 'react'
import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  CheckCircleOutlined,
  CheckOutlined,
  CloseCircleOutlined,
  SearchOutlined,
} from '@ant-design/icons'
import { immediateReportingObligationSectors, sectors } from '../config/sectors'
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react'
import dayjs from 'dayjs'
import { CSSTransition, SwitchTransition } from 'react-transition-group'
import { errorResponse } from '../lib/errorResponse'
import { GermanFlag } from '../components/Qualification/GermanFlag'
import { InternationalIcon } from '../components/Qualification/InternationalIcon'
import { useSearchParams } from 'react-router-dom'
import { Trans, useTranslation } from 'react-i18next'
import { useLightOnlyRoute } from '../lib/themeController'

export function QualificationPage() {
  const { t } = useTranslation()
  const firestore = useFirestore()
  const functions = useFunctions()

  // Brand-designed funnel: always light, whatever the saved preference.
  useLightOnlyRoute()
  useDocumentTitle(`cleverlohn`)

  function getUid() {
    let uid = localStorage.getItem('qualificationUid')
    if (!uid) {
      uid = doc(collection(firestore, 'qualifications')).id
      localStorage.setItem('qualificationUid', uid)
    }
    return uid
  }

  const [data, setData] = useState({})
  const [step, setStep] = useState(0)

  const [searchParams] = useSearchParams()

  const uid = useMemo(() => getUid(), [])

  useEffect(() => {
    const companyName = searchParams.get('companyName')
    const email = searchParams.get('email')
    if (companyName || email) {
      addData({ companyName, email })
      // noinspection JSIgnoredPromiseFromCall
      saveToFirestore({ companyName, email })
    }
  }, [])

  const saveToFirestore = async (values) => {
    try {
      await api(functions, 'qualifications/save', {
        qualificationUid: uid,
        qualificationData: {
          finished: values.finished ?? false,
          response: values.response ?? null,
          lastStep: values.lastStep ?? null,
          companyName: values.companyName ?? null,
          payrollCountries: values.payrollCountries ?? null,
          sector: values.sector ?? null,
          immediateReportingObligation:
            values.immediateReportingObligation ?? null,
          salaryTypes: values.salaryTypes ?? null,
          employeeCount: values.employeeCount ?? null,
          currentPayrollAccounting: values.currentPayrollAccounting ?? null,
          hasEmployedWorkersThisYear: values.hasEmployedWorkersThisYear ?? null,
          fixedPayrollDay: values.fixedPayrollDay ?? null,
          payrollMonth: values.payrollMonth ?? null,
          payrollDay: values.payrollDay ?? null,
          switchReason: values.switchReason ?? null,
          switchDate: values.switchDate ?? null,
          firstMonth: values.firstMonth ?? null,
          contactName: values.contactName ?? null,
          phoneNumber: values.phoneNumber ?? null,
          email: values.email ?? null,
          deleted: false,
        },
      })
    } catch (error) {
      errorResponse(error)
      throw error
    }
  }

  function addData(data) {
    setData((current) => {
      const newData = { ...current, ...data }
      if (newData.companyName || newData.email) {
        // noinspection JSIgnoredPromiseFromCall
        saveToFirestore(newData)
      }
      return newData
    })
  }

  async function next(newData, finished = false) {
    if (finished) {
      await saveToFirestore({ ...data, ...newData, finished: true })
      localStorage.removeItem('qualificationUid')
    } else {
      addData(newData)
    }

    setStep((current) => current + 1)
  }

  function previous() {
    setStep((current) => current - 1)
  }

  const { token } = theme.useToken()

  return (
    <ConfigProvider
      theme={{
        token: {
          // theme-ok: brand funnel, always light (useLightOnlyRoute)
          colorPrimary: '#D1F500',
          colorTextLightSolid: '#000000', // theme-ok: brand funnel, always light
          controlHeightLG: 48,
          paddingContentHorizontal: 32,
          colorPrimaryHover: '#c0e04a', // theme-ok: brand funnel, always light
        },
      }}
    >
      <Layout className={styles.baseLayoutQualification}>
        <Layout.Content className={styles.contentQualification}>
          <div style={{ height: 50 }}>
            <CSSTransition in={step > 0} key="logo" appear timeout={150}>
              {(state) => (
                <SlideDiv
                  state={state}
                  direction="Y"
                  entering={-50}
                  exiting={50}
                >
                  <img src={logo} style={{ height: 20 }} alt="cleverlohn" />
                </SlideDiv>
              )}
            </CSSTransition>
          </div>
          <div style={{ maxWidth: 500, width: '100%', marginTop: 'auto' }}>
            <Steps
              data={data}
              step={step}
              next={next}
              previous={previous}
              addData={addData}
            />
          </div>
          <div style={{ marginTop: 'auto', paddingTop: token.marginXXL }}>
            <Space size="middle">
              <a
                href="https://cleverlohn.de/agb"
                target="_blank"
                style={{ color: token.colorTextSecondary }}
                rel="noreferrer"
              >
                {t('frontend:qualification.agb')}
              </a>
              <a
                href="https://cleverlohn.de/impressum"
                target="_blank"
                style={{ color: token.colorTextSecondary }}
                rel="noreferrer"
              >
                {t('frontend:qualification.impressum')}
              </a>
              <a
                href="https://cleverlohn.de/datenschutz"
                target="_blank"
                style={{ color: token.colorTextSecondary }}
                rel="noreferrer"
              >
                {t('frontend:qualification.datenschutz')}
              </a>
            </Space>
          </div>
        </Layout.Content>
      </Layout>
    </ConfigProvider>
  )
}

function SlideDiv({
  children,
  state,
  direction = 'X',
  exiting = -50,
  entering = 50,
}) {
  return (
    <div
      style={{
        transition: '0.5s',
        opacity: state === 'entered' ? 1 : 0,
        transform: `translate${direction}(${
          state === 'entered'
            ? 0
            : state === 'exiting'
              ? `${exiting}px`
              : `${entering}px`
        })`,
        display: state === 'exited' ? 'none' : 'block',
      }}
    >
      {children}
    </div>
  )
}

function Steps({ data, step, next, previous, addData }) {
  // const immediateReportingObligation = sectors.find(
  //   (s) => s.key === data.sector
  // )?.immediateReportingObligation
  //
  // const germanPayrollCountries = data.payrollCountries === 'Germany'
  // const onlyStandardSalaryTypes =
  //   !data?.salaryTypes?.includes('Construction') &&
  //   !data?.salaryTypes?.includes('Insolvency')
  const noEmployees = data.employeeCount === '0'
  // const oneToFiftyEmployees =
  //   data.employeeCount === '1-19' || data.employeeCount === '20-49'

  // const { token } = theme.useToken()

  const steps = []

  steps.push(<StepStart next={next} />)

  steps.push(
    <StepContact
      data={data}
      next={next}
      previous={previous}
      text={
        <>
          {/*<Typography.Paragraph strong>*/}
          {/*  <Space size="middle">*/}
          {/*<CheckCircleOutlined*/}
          {/*  style={{ fontSize: '2em', color: token.colorSuccess }}*/}
          {/*/>*/}
          {/*<span>*/}
          {/*      Wir würden uns freuen, die Lohnabrechnung für dein*/}
          {/*    Unternehmen zu übernehmen!*/}
          {/*</span>*/}
          {/*</Space>*/}
          {/*</Typography.Paragraph>*/}
          {/*<Typography.Title level={5}>*/}
          {/*  Zuerst einmal brauchen wir deine Kontaktdaten, damit wir dich*/}
          {/*  erreichen können.*/}
          {/*</Typography.Title>*/}
        </>
      }
    />
  )

  // steps.push(<StepName data={data} next={next} />)
  steps.push(
    <StepPayrollCountries data={data} next={next} previous={previous} />
  )

  // if (germanPayrollCountries) {
  // steps.push(<StepSector data={data} next={next} previous={previous} />)

  // if (immediateReportingObligation) {
  steps.push(
    <StepImmediateReportingObligation
      data={data}
      next={next}
      previous={previous}
    />
  )
  // }

  steps.push(<StepSalaryTypes data={data} next={next} previous={previous} />)
  steps.push(<StepEmployeeCount data={data} next={next} previous={previous} />)

  // if (onlyStandardSalaryTypes) {

  // if (noEmployees || oneToFiftyEmployees) {
  if (noEmployees) {
    steps.push(
      <StepHasEmployedWorkersThisYear
        data={data}
        next={next}
        previous={previous}
      />
    )
  }

  // if (
  //   (noEmployees && data.hasEmployedWorkersThisYear) ||
  //   oneToFiftyEmployees
  // ) {
  steps.push(
    <StepCurrentPayrollDay data={data} next={next} previous={previous} />
  )
  steps.push(
    <StepCurrentPayrollAccounting data={data} next={next} previous={previous} />
  )
  // }

  // steps.push(
  //   <StepSwitchReason data={data} next={next} previous={previous} />
  // )

  steps.push(
    <StepFirstMonth
      data={data}
      next={(values) => next(values, true)} // Next step is end
      previous={previous}
    />
  )

  steps.push(<StepEnd data={data} />)
  // } else {
  //   steps.push(
  //     <StepContact
  //       data={data}
  //       next={next}
  //       previous={previous}
  //       showInPersonCalendly
  //       text={
  //         <>
  //           <Typography.Paragraph strong>
  //             <Space size="middle">
  //               <PhoneOutlined
  //                 style={{ fontSize: '2em', color: token.colorSuccess }}
  //               />
  //               <span>
  //                 Für Unternehmen mit mehr als 50 Angestellten erstellen wir
  //                 gerne ein persönliches Angebot.
  //               </span>
  //             </Space>
  //           </Typography.Paragraph>
  //           <Typography.Paragraph>
  //             Um weitere Details zu besprechen, würden wir uns gerne
  //             persönlich mit dir in Verbindung setzen. Bitte teile uns deine
  //             Kontaktdaten mit, damit wir dich erreichen können.
  //           </Typography.Paragraph>
  //         </>
  //       }
  //     />
  //   )
  //   steps.push(<StepEnd />)
  // }
  // } else {
  //   steps.push(
  //     <StepContact
  //       data={data}
  //       next={next}
  //       previous={previous}
  //       text={
  //         <>
  //           <Typography.Paragraph strong>
  //             <Space size='middle'>
  //               <FrownOutlined
  //                 style={{ fontSize: '2em', color: token.colorWarning }}
  //               />
  //               <span>
  //                   Momentan bieten wir leider noch keine Abrechnung für Bau-
  //                   und Insolvenzlöhne an. Wir arbeiten jedoch stetig daran,
  //                   unser Angebot zu erweitern.
  //                 </span>
  //             </Space>
  //           </Typography.Paragraph>
  //           <Typography.Paragraph>
  //             Bitte hinterlasse uns deine Kontaktdaten, und wir werden uns
  //             gerne bei dir melden, sobald wir dir damit weiterhelfen können.
  //           </Typography.Paragraph>
  //         </>
  //       }
  //     />,
  //   )
  //   steps.push(<StepEnd />)
  // }
  // }
  //
  // else
  // {
  //   steps.push(
  //     <StepContact
  //       data={data}
  //       next={next}
  //       previous={previous}
  //       text={
  //         <>
  //           <Typography.Paragraph strong>
  //             <Space size='middle'>
  //               <FrownOutlined
  //                 style={{ fontSize: '2em', color: token.colorWarning }}
  //               />
  //               <span>
  //                   Leider bieten wir noch keine Abrechnungen für ausländische
  //                   Löhne an. Wir arbeiten jedoch jederzeit daran, unser
  //                   Leistungsangebot zu erweitern.
  //                 </span>
  //             </Space>
  //           </Typography.Paragraph>
  //           <Typography.Paragraph>
  //             Wir schätzen dein Interesse an unseren Dienstleistungen und werden
  //             uns gerne bei dir melden, sobald wir Abrechnungen für ausländische
  //             Löhne anbieten können.
  //           </Typography.Paragraph>
  //           <Typography.Paragraph>
  //             Wenn du nur deine deutschen Löhne abrechnen lassen willst, klicke
  //             hier:
  //           </Typography.Paragraph>
  //           <Typography.Paragraph>
  //             <Button
  //               size='large'
  //               block
  //               icon={<Icon component={GermanFlag} />}
  //               onClick={() => addData({ payrollCountries: 'Germany' })}
  //               style={{ whiteSpace: 'normal', height: 'auto' }}
  //             >
  //               Ich möchte nur deutsche Löhne abrechnen
  //             </Button>
  //           </Typography.Paragraph>
  //         </>
  //       }
  //     />,
  //   )
  //   steps.push(<StepEnd />)
  // }

  const SlideTransition = ({ children, ...rest }) => {
    return (
      <CSSTransition {...rest} appear={true} timeout={150}>
        {(state) => <SlideDiv state={state}>{children}</SlideDiv>}
      </CSSTransition>
    )
  }

  return (
    <>
      <SwitchTransition mode="out-in">
        <SlideTransition key={`${step}-${JSON.stringify(data)}`}>
          {steps?.[step] ?? null}
        </SlideTransition>
      </SwitchTransition>
    </>
  )
}

function StepStart({ next }) {
  const { t } = useTranslation()
  const { token } = theme.useToken()
  return (
    <Space direction="vertical">
      <img
        src={logo}
        style={{ height: 30, marginBottom: token.marginXL }}
        alt="cleverlohn"
      />
      <div style={{ maxWidth: '35em', marginBottom: token.marginLG }}>
        <Typography.Text style={{ fontSize: token.fontSizeLG }}>
          <Trans
            i18nKey="frontend:qualification.startIntro"
            components={{ br: <br /> }}
          />
        </Typography.Text>
      </div>
      <Button
        type="primary"
        size="large"
        onClick={() => next({ lastStep: 'start' })}
        style={{ whiteSpace: 'normal', height: 'auto' }}
      >
        {t('frontend:qualification.letsGo')} <ArrowRightOutlined />
      </Button>
    </Space>
  )
}

function StepName({ next, data }) {
  const { t } = useTranslation()
  const { token } = theme.useToken()

  const [form] = Form.useForm()

  function onFinish(values) {
    next({ ...values, lastStep: 'name' })
  }

  return (
    <Form
      layout="vertical"
      colon={false}
      requiredMark={false}
      form={form}
      onFinish={onFinish}
      initialValues={data}
    >
      <Row gutter={gutter}>
        <Col span={24}>
          <Typography.Title>
            {t('frontend:qualification.heading')}
          </Typography.Title>
        </Col>
        <Col span={24}>
          <Typography.Text>
            {t('frontend:qualification.nameIntro')}
          </Typography.Text>
        </Col>

        <Col span={24}>
          <Form.Item
            name={['companyName']}
            label={t('frontend:qualification.companyName')}
            style={{ marginTop: token.marginXL }}
            rules={[
              {
                required: true,
                message: t('frontend:qualification.companyNameRequired'),
              },
              {
                max: 100,
                message: t('frontend:qualification.max100'),
              },
            ]}
          >
            <Input
              size="large"
              placeholder={t('frontend:qualification.companyNamePlaceholder')}
            />
          </Form.Item>
        </Col>

        <Col span={24} style={{ textAlign: 'right' }}>
          <Button type="primary" size="large" htmlType="submit">
            {t('frontend:qualification.next')} <ArrowRightOutlined />
          </Button>
        </Col>
      </Row>
    </Form>
  )
}

function StepPayrollCountries({ next, previous, data }) {
  const { t } = useTranslation()
  const { token } = theme.useToken()

  const [form] = Form.useForm()

  function onFinish(values) {
    next({ ...values, lastStep: 'payrollCountries' })
  }

  return (
    <Form
      layout="vertical"
      colon={false}
      requiredMark={false}
      form={form}
      onFinish={onFinish}
      initialValues={data}
    >
      <Row gutter={gutter}>
        <Col span={24}>
          <Typography.Title>
            {t('frontend:qualification.heading')}
          </Typography.Title>
        </Col>
        <Col span={24}>
          <Typography.Text>
            {t('frontend:qualification.payrollCountriesQuestion')}
          </Typography.Text>
        </Col>

        <Col span={24}>
          <ConfigProvider
            theme={{
              components: {
                Button: {
                  controlHeight: token.controlHeightLG * 2,
                },
              },
            }}
          >
            <Form.Item
              name={['payrollCountries']}
              style={{ marginTop: token.marginXL }}
              rules={[
                {
                  required: true,
                  message: t('frontend:qualification.selectRequired'),
                },
              ]}
            >
              <ButtonSelect
                items={[
                  {
                    key: 'Germany',
                    label: (
                      <>
                        <GermanFlag
                          style={{
                            width: '50%',
                            margin: '0 auto',
                            display: 'block',
                            marginBottom: token.marginLG,
                          }}
                        />
                        <Trans
                          i18nKey="frontend:qualification.payrollCountriesGermany"
                          components={{ strong: <strong />, br: <br /> }}
                        />
                      </>
                    ),
                  },
                  {
                    key: 'International',
                    label: (
                      <>
                        <InternationalIcon
                          style={{
                            width: '50%',
                            margin: '0 auto',
                            display: 'block',
                            marginBottom: token.marginLG,
                          }}
                        />
                        {t('frontend:qualification.payrollCountriesInternational')}
                      </>
                    ),
                  },
                ]}
              />
            </Form.Item>
          </ConfigProvider>
        </Col>

        <Col
          span={24}
          style={{ display: 'flex', justifyContent: 'space-between' }}
        >
          <Button size="large" onClick={previous}>
            <ArrowLeftOutlined /> {t('frontend:common.back')}
          </Button>
          <Button type="primary" size="large" htmlType="submit">
            {t('frontend:qualification.next')} <ArrowRightOutlined />
          </Button>
        </Col>
      </Row>
    </Form>
  )
}

function StepSector({ next, previous, data }) {
  const { t } = useTranslation()
  const [form] = Form.useForm()

  function onFinish(values) {
    next({ ...values, lastStep: 'sector' })
  }

  const [search, setSearch] = useState('')

  const filteredSectors = sectors.filter(({ label }) => {
    if (search) {
      return label.toLowerCase().includes(search.toLowerCase())
    } else {
      return true
    }
  })

  return (
    <Form
      layout="vertical"
      colon={false}
      requiredMark={false}
      form={form}
      onFinish={onFinish}
      initialValues={data}
    >
      <Row gutter={gutter}>
        <Col span={24}>
          <Typography.Title>
            {t('frontend:qualification.heading')}
          </Typography.Title>
        </Col>
        <Col span={24}>
          <Typography.Text>
            {t('frontend:qualification.sectorQuestion')}
          </Typography.Text>
        </Col>

        <Col span={24}>
          <Input
            size="large"
            style={{ width: '100%' }}
            prefix={<SearchOutlined />}
            onChange={(e) => setSearch(e.target.value)}
          />
        </Col>

        <Col span={24}>
          <Form.Item
            name={['sector']}
            rules={[
              {
                required: true,
                message: t('frontend:qualification.selectRequired'),
              },
            ]}
          >
            <ButtonSelect
              items={filteredSectors}
              span={24}
              maxHeight={400}
              buttonStyle={{
                marginBottom: 0,
              }}
              miscSelection={t('frontend:qualification.sectorNotFound')}
            />
          </Form.Item>
        </Col>

        <Col
          span={24}
          style={{ display: 'flex', justifyContent: 'space-between' }}
        >
          <Button size="large" onClick={previous}>
            <ArrowLeftOutlined /> {t('frontend:common.back')}
          </Button>
          <Button type="primary" size="large" htmlType="submit">
            {t('frontend:qualification.next')} <ArrowRightOutlined />
          </Button>
        </Col>
      </Row>
    </Form>
  )
}

function StepSalaryTypes({ next, previous, data }) {
  const { t } = useTranslation()
  const { token } = theme.useToken()

  const [form] = Form.useForm()

  function onFinish(values) {
    next({ ...values, lastStep: 'salaryTypes' })
  }

  return (
    <Form
      layout="vertical"
      colon={false}
      requiredMark={false}
      form={form}
      onFinish={onFinish}
      initialValues={data}
    >
      <Row gutter={gutter}>
        <Col span={24}>
          <Typography.Title>
            {t('frontend:qualification.heading')}
          </Typography.Title>
        </Col>
        <Col span={24}>
          <Space direction="vertical">
            <Typography.Text>
              {t('frontend:qualification.salaryTypesQuestion')}
            </Typography.Text>
            <Typography.Text type="secondary">
              {t('frontend:qualification.multiSelectHint')}
            </Typography.Text>
          </Space>
        </Col>

        <Col span={24}>
          <ConfigProvider
            theme={{
              components: {
                Button: {
                  controlHeight: token.controlHeightLG,
                },
              },
            }}
          >
            <Form.Item
              name={['salaryTypes']}
              style={{ marginTop: token.marginXL }}
              rules={[
                {
                  required: true,
                  message: t('frontend:qualification.selectRequired'),
                },
              ]}
            >
              <ButtonSelect
                span={24}
                multiple
                items={[
                  {
                    key: 'Fixed',
                    label: t('frontend:qualification.salaryTypeFixed'),
                  },
                  {
                    key: 'Hourly',
                    label: t('frontend:qualification.salaryTypeHourly'),
                  },
                  {
                    key: 'Construction',
                    label: t('frontend:qualification.salaryTypeConstruction'),
                  },
                  {
                    key: 'Insolvency',
                    label: t('frontend:qualification.salaryTypeInsolvency'),
                  },
                ]}
              />
            </Form.Item>
          </ConfigProvider>
        </Col>

        <Col
          span={24}
          style={{ display: 'flex', justifyContent: 'space-between' }}
        >
          <Button size="large" onClick={previous}>
            <ArrowLeftOutlined /> {t('frontend:common.back')}
          </Button>
          <Button type="primary" size="large" htmlType="submit">
            {t('frontend:qualification.next')} <ArrowRightOutlined />
          </Button>
        </Col>
      </Row>
    </Form>
  )
}

function StepImmediateReportingObligation({ next, previous, data }) {
  const { t } = useTranslation()
  const { token } = theme.useToken()

  const [form] = Form.useForm()

  function onFinish(values) {
    next({ ...values, lastStep: 'immediateReportingObligation' })
  }

  return (
    <Form
      layout="vertical"
      colon={false}
      requiredMark={false}
      form={form}
      onFinish={onFinish}
      initialValues={data}
    >
      <Row gutter={gutter}>
        <Col span={24}>
          <Typography.Title>
            {t('frontend:qualification.heading')}
          </Typography.Title>
        </Col>
        <Col span={24}>
          <Typography.Text>
            {t('frontend:qualification.immediateReportingQuestion')}
            <Collapse
              style={{ marginTop: token.marginMD }}
              items={[
                {
                  key: 'sectors',
                  label: t('frontend:qualification.affectedSectors'),
                  children: (
                    <>
                      <ul style={{ marginLeft: token.marginSM }}>
                        {immediateReportingObligationSectors.map(({ name }) => (
                          <li key={name}>{name}</li>
                        ))}
                      </ul>
                    </>
                  ),
                },
              ]}
            />
          </Typography.Text>
        </Col>

        <Col span={24}>
          <ConfigProvider
            theme={{
              components: {
                Button: {
                  controlHeight: token.controlHeightLG * 2,
                },
              },
            }}
          >
            <Form.Item
              name={['immediateReportingObligation']}
              style={{ marginTop: token.marginXS }}
              rules={[
                {
                  required: true,
                  message: t('frontend:qualification.selectRequired'),
                },
              ]}
            >
              <ButtonSelect
                items={[
                  {
                    key: true,
                    label: (
                      <>
                        <CheckCircleOutlined
                          style={{
                            fontSize: '50px',
                            marginBottom: token.marginSM,
                            display: 'block',
                          }}
                        />
                        {t('frontend:qualification.immediateReportingYes')}
                      </>
                    ),
                  },
                  {
                    key: false,
                    label: (
                      <>
                        <CloseCircleOutlined
                          style={{
                            fontSize: '50px',
                            marginBottom: token.marginSM,
                            display: 'block',
                          }}
                        />
                        <Trans
                          i18nKey="frontend:qualification.immediateReportingNo"
                          components={{ strong: <strong /> }}
                        />
                      </>
                    ),
                  },
                ]}
              />
            </Form.Item>
          </ConfigProvider>
        </Col>

        <Col
          span={24}
          style={{ display: 'flex', justifyContent: 'space-between' }}
        >
          <Button size="large" onClick={previous}>
            <ArrowLeftOutlined /> {t('frontend:common.back')}
          </Button>
          <Button type="primary" size="large" htmlType="submit">
            {t('frontend:qualification.next')} <ArrowRightOutlined />
          </Button>
        </Col>
      </Row>
    </Form>
  )
}

function StepEmployeeCount({ next, previous, data }) {
  const { t } = useTranslation()
  const { token } = theme.useToken()

  const [form] = Form.useForm()

  function onFinish(values) {
    next({ ...values, lastStep: 'employeeCount' })
  }

  return (
    <Form
      layout="vertical"
      colon={false}
      requiredMark={false}
      form={form}
      onFinish={onFinish}
      initialValues={data}
    >
      <Row gutter={gutter}>
        <Col span={24}>
          <Typography.Title>
            {t('frontend:qualification.heading')}
          </Typography.Title>
        </Col>
        <Col span={24}>
          <Typography.Text>
            {t('frontend:qualification.employeeCountQuestion')}
          </Typography.Text>
        </Col>

        <Col span={24}>
          <ConfigProvider
            theme={{
              components: {
                Button: {
                  controlHeight: token.controlHeightLG * 2,
                },
              },
            }}
          >
            <Form.Item
              name={['employeeCount']}
              style={{ marginTop: token.marginXL }}
              rules={[
                {
                  required: true,
                  message: t('frontend:qualification.selectRequired'),
                },
              ]}
            >
              <ButtonSelect
                buttonStyle={{
                  textAlign: 'left',
                }}
                items={[
                  {
                    key: '0',
                    label: (
                      <Trans
                        i18nKey="frontend:qualification.employeeCount0"
                        components={{ strong: <strong /> }}
                      />
                    ),
                  },
                  {
                    key: '1-4',
                    label: (
                      <Trans
                        i18nKey="frontend:qualification.employeeCount1to4"
                        components={{ strong: <strong /> }}
                      />
                    ),
                  },
                  {
                    key: '5-19',
                    label: (
                      <Trans
                        i18nKey="frontend:qualification.employeeCount5to19"
                        components={{ strong: <strong /> }}
                      />
                    ),
                  },
                  {
                    key: '20-49',
                    label: (
                      <Trans
                        i18nKey="frontend:qualification.employeeCount20to49"
                        components={{ strong: <strong /> }}
                      />
                    ),
                  },
                  {
                    key: '50-99',
                    label: (
                      <Trans
                        i18nKey="frontend:qualification.employeeCount50to99"
                        components={{ strong: <strong /> }}
                      />
                    ),
                  },
                  {
                    key: '100-499',
                    label: (
                      <Trans
                        i18nKey="frontend:qualification.employeeCount100to499"
                        components={{ strong: <strong /> }}
                      />
                    ),
                  },
                  {
                    key: '500+',
                    label: (
                      <Row justify="space-between">
                        <Col>
                          <Trans
                            i18nKey="frontend:qualification.employeeCount500plus"
                            components={{ strong: <strong /> }}
                          />
                        </Col>
                        <Col>{/*<UserGroupIcon />*/}</Col>
                      </Row>
                    ),
                  },
                ]}
              />
            </Form.Item>
          </ConfigProvider>
        </Col>

        <Col
          span={24}
          style={{ display: 'flex', justifyContent: 'space-between' }}
        >
          <Button size="large" onClick={previous}>
            <ArrowLeftOutlined /> {t('frontend:common.back')}
          </Button>
          <Button type="primary" size="large" htmlType="submit">
            {t('frontend:qualification.next')} <ArrowRightOutlined />
          </Button>
        </Col>
      </Row>
    </Form>
  )
}

function ButtonSelect({
  value,
  items,
  onChange,
  multiple = false,
  span = 12,
  buttonStyle = {},
  maxHeight = null,
  miscSelection,
  ...props
}) {
  let miscButton = null
  if (miscSelection) {
    miscButton = (
      <Button
        type={
          (multiple ? value?.includes('Misc') : value === 'Misc')
            ? 'primary'
            : 'default'
        }
        block
        danger={props['aria-invalid']}
        style={{ whiteSpace: 'normal', height: '100%', ...buttonStyle }}
        onClick={() => onChange(multiple ? ['Misc'] : 'Misc')}
      >
        {miscSelection}
      </Button>
    )
  }

  const buttons = (
    <Row gutter={gutter}>
      {items.map(({ key, label }) => (
        <Col xs={24} sm={span} key={key}>
          <Button
            danger={props['aria-invalid']}
            type={
              multiple
                ? value?.includes(key)
                  ? 'primary'
                  : 'default'
                : value === key
                  ? 'primary'
                  : 'default'
            }
            block
            style={{ whiteSpace: 'normal', height: '100%', ...buttonStyle }}
            onClick={() =>
              onChange(
                multiple
                  ? (value?.includes(key)
                      ? value.filter((v) => v !== key)
                      : [...(value ?? []), key]
                    ).filter((v) => v !== 'Misc')
                  : key
              )
            }
          >
            {label}
            {multiple ? value?.includes(key) ? <CheckOutlined /> : null : null}
          </Button>
        </Col>
      ))}
      {miscButton && !maxHeight && (
        <Col xs={24} sm={span}>
          {miscButton}
        </Col>
      )}
    </Row>
  )

  if (maxHeight) {
    return (
      <>
        <OverlayScrollbarsComponent
          options={{
            overflow: {
              x: 'hidden',
            },
          }}
          style={{ maxHeight, minHeight: maxHeight, paddingRight: 20 }}
        >
          {buttons}
        </OverlayScrollbarsComponent>

        {miscSelection && (
          <>
            <Divider />
            {miscButton}
          </>
        )}
      </>
    )
  } else {
    return buttons
  }
}

function StepHasEmployedWorkersThisYear({ next, previous, data }) {
  const { t } = useTranslation()
  const { token } = theme.useToken()

  const [form] = Form.useForm()

  function onFinish(values) {
    next({ ...values, lastStep: 'hasEmployedWorkersThisYear' })
  }

  return (
    <Form
      layout="vertical"
      colon={false}
      requiredMark={false}
      form={form}
      onFinish={onFinish}
      initialValues={data}
    >
      <Row gutter={gutter}>
        <Col span={24}>
          <Typography.Title>
            {t('frontend:qualification.heading')}
          </Typography.Title>
        </Col>
        <Col span={24}>
          <Typography.Text>
            {t('frontend:qualification.hasEmployedWorkersQuestion')}
          </Typography.Text>
        </Col>

        <Col span={24}>
          <ConfigProvider
            theme={{
              components: {
                Button: {
                  controlHeight: token.controlHeightLG * 2,
                },
              },
            }}
          >
            <Form.Item
              name={['hasEmployedWorkersThisYear']}
              style={{ marginTop: token.marginXS }}
              rules={[
                {
                  required: true,
                  message: t('frontend:qualification.selectRequired'),
                },
              ]}
            >
              <ButtonSelect
                items={[
                  {
                    key: true,
                    label: (
                      <>
                        <CheckCircleOutlined
                          style={{
                            fontSize: '50px',
                            marginBottom: token.marginSM,
                            display: 'block',
                          }}
                        />
                        {t('frontend:common.yes')}
                      </>
                    ),
                  },
                  {
                    key: false,
                    label: (
                      <>
                        <CloseCircleOutlined
                          style={{
                            fontSize: '50px',
                            marginBottom: token.marginSM,
                            display: 'block',
                          }}
                        />
                        {t('frontend:common.no')}
                      </>
                    ),
                  },
                ]}
              />
            </Form.Item>
          </ConfigProvider>
        </Col>

        <Col
          span={24}
          style={{ display: 'flex', justifyContent: 'space-between' }}
        >
          <Button size="large" onClick={previous}>
            <ArrowLeftOutlined /> {t('frontend:common.back')}
          </Button>
          <Button type="primary" size="large" htmlType="submit">
            {t('frontend:qualification.next')} <ArrowRightOutlined />
          </Button>
        </Col>
      </Row>
    </Form>
  )
}

function StepCurrentPayrollDay({ next, previous, data }) {
  const { t } = useTranslation()
  const { token } = theme.useToken()

  const [form] = Form.useForm()

  function onFinish(values) {
    next({ ...values, lastStep: 'currentPayrollDay' })
  }

  const fixedPayrollDay = Form.useWatch(['fixedPayrollDay'], {
    form,
    preserve: true,
  })

  const payrollMonth = Form.useWatch(['payrollMonth'], {
    form,
  })

  return (
    <Form
      layout="vertical"
      colon={false}
      requiredMark={false}
      form={form}
      onFinish={onFinish}
      initialValues={data}
    >
      <Row gutter={gutter}>
        <Col span={24}>
          <Typography.Title>
            {t('frontend:qualification.heading')}
          </Typography.Title>
        </Col>
        <Col span={24}>
          <Typography.Text>
            {t('frontend:qualification.currentPayrollDayIntro')}
          </Typography.Text>
        </Col>
        <ConfigProvider
          theme={{
            components: {
              Button: {
                controlHeight: token.controlHeightLG,
              },
            },
          }}
        >
          <Col span={24}>
            <Form.Item
              name={['payrollMonth']}
              style={{ marginTop: token.marginXS }}
              labelAlign="left"
              label={
                <Typography.Text type="secondary">
                  {t('frontend:qualification.chooseOption')}
                </Typography.Text>
              }
              rules={[
                {
                  required: true,
                  message: t('frontend:qualification.selectRequired'),
                },
              ]}
            >
              <ButtonSelect
                span={24}
                buttonStyle={{ textAlign: 'left' }}
                items={[
                  {
                    key: 'AccountingMonth',
                    label: (
                      <Trans
                        i18nKey="frontend:qualification.payrollMonthAccounting"
                        components={{ strong: <strong /> }}
                      />
                    ),
                  },
                  {
                    key: 'FollowingMonth',
                    label: (
                      <Trans
                        i18nKey="frontend:qualification.payrollMonthFollowing"
                        components={{ strong: <strong /> }}
                      />
                    ),
                  },
                  {
                    key: 'Unknown',
                    label: <>{t('frontend:qualification.dontKnow')}</>,
                  },
                ]}
              />
            </Form.Item>
          </Col>
          {payrollMonth && payrollMonth !== 'Unknown' && (
            <>
              <Col span={24}>
                <Form.Item
                  name={['fixedPayrollDay']}
                  style={{ marginTop: token.marginXS }}
                  label={t('frontend:qualification.fixedPayrollDayQuestion')}
                  labelAlign="left"
                  rules={[
                    {
                      required: true,
                      message: t('frontend:qualification.selectRequired'),
                    },
                  ]}
                >
                  <ButtonSelect
                    items={[
                      {
                        key: true,
                        label: <>{t('frontend:common.yes')}</>,
                      },
                      {
                        key: false,
                        label: (
                          <>{t('frontend:qualification.noOrDontKnow')}</>
                        ),
                      },
                    ]}
                  />
                </Form.Item>
              </Col>
              {fixedPayrollDay && (
                <Col span={24}>
                  <Form.Item
                    name={['payrollDay']}
                    style={{ marginTop: token.marginXS }}
                    labelAlign="left"
                    dependencies={['payrollMonth']}
                    label={
                      <Typography.Text type="secondary">
                        {t('frontend:qualification.payrollDayQuestion')}
                      </Typography.Text>
                    }
                    rules={[
                      {
                        required: true,
                        message: t('frontend:qualification.payrollDayRequired'),
                      },
                      // Validate that the day is max 10 if the payrollMonth is FollowingMonth
                      ...(payrollMonth === 'FollowingMonth'
                        ? [
                            {
                              type: 'integer',
                              min: 1,
                              max: 10,
                              message: t(
                                'frontend:qualification.payrollDayFollowingMonthMax'
                              ),
                            },
                          ]
                        : [
                            {
                              type: 'integer',
                              min: 1,
                              max: 31,
                              message: t(
                                'frontend:qualification.payrollDayRange'
                              ),
                            },
                          ]),
                    ]}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      controls={false}
                      placeholder="31."
                      step={1}
                      size="large"
                      addonBefore={t('frontend:qualification.payrollDayAddonBefore')}
                      addonAfter={t('frontend:qualification.payrollDayAddonAfter')}
                    />
                  </Form.Item>
                </Col>
              )}
            </>
          )}
          {fixedPayrollDay && <></>}
        </ConfigProvider>

        <Col
          span={24}
          style={{ display: 'flex', justifyContent: 'space-between' }}
        >
          <Button size="large" onClick={previous}>
            <ArrowLeftOutlined /> {t('frontend:common.back')}
          </Button>
          <Button type="primary" size="large" htmlType="submit">
            {t('frontend:qualification.next')} <ArrowRightOutlined />
          </Button>
        </Col>
      </Row>
    </Form>
  )
}

function StepCurrentPayrollAccounting({ next, previous, data }) {
  const { t } = useTranslation()
  const { token } = theme.useToken()

  const [form] = Form.useForm()

  function onFinish(values) {
    next({ ...values, lastStep: 'currentPayrollAccounting' })
  }

  return (
    <Form
      layout="vertical"
      colon={false}
      requiredMark={false}
      form={form}
      onFinish={onFinish}
      initialValues={data}
    >
      <Row gutter={gutter}>
        <Col span={24}>
          <Typography.Title>
            {t('frontend:qualification.heading')}
          </Typography.Title>
        </Col>
        <Col span={24}>
          <Typography.Text>
            {t('frontend:qualification.currentPayrollAccountingQuestion')}
          </Typography.Text>
        </Col>

        <Col span={24}>
          <ConfigProvider
            theme={{
              components: {
                Button: {
                  controlHeight: token.controlHeightLG,
                },
              },
            }}
          >
            <Form.Item
              name={['currentPayrollAccounting']}
              style={{ marginTop: token.marginXS }}
              rules={[
                {
                  required: true,
                  message: t('frontend:qualification.selectRequired'),
                },
              ]}
            >
              <ButtonSelect
                span={24}
                miscSelection={t('frontend:qualification.payrollAccountingOther')}
                items={[
                  {
                    key: 'TaxAccountant',
                    label: (
                      <>
                        {t('frontend:qualification.payrollAccountingTaxAccountant')}
                      </>
                    ),
                  },
                  {
                    key: 'PayrollOffice',
                    label: (
                      <>
                        {t('frontend:qualification.payrollAccountingPayrollOffice')}
                      </>
                    ),
                  },
                  {
                    key: 'SelfSoftware',
                    label: (
                      <>
                        {t('frontend:qualification.payrollAccountingSelfSoftware')}
                      </>
                    ),
                  },
                ]}
              />
            </Form.Item>
          </ConfigProvider>
        </Col>

        <Col
          span={24}
          style={{ display: 'flex', justifyContent: 'space-between' }}
        >
          <Button size="large" onClick={previous}>
            <ArrowLeftOutlined /> {t('frontend:common.back')}
          </Button>
          <Button type="primary" size="large" htmlType="submit">
            {t('frontend:qualification.next')} <ArrowRightOutlined />
          </Button>
        </Col>
      </Row>
    </Form>
  )
}

function StepSwitchReason({ next, previous, data }) {
  const { t } = useTranslation()
  const { token } = theme.useToken()

  const [form] = Form.useForm()

  function onFinish(values) {
    next({ ...values, lastStep: 'switchReason' })
  }

  return (
    <Form
      layout="vertical"
      colon={false}
      requiredMark={false}
      form={form}
      onFinish={onFinish}
      initialValues={data}
    >
      <Row gutter={gutter}>
        <Col span={24}>
          <Typography.Title>
            {t('frontend:qualification.heading')}
          </Typography.Title>
        </Col>
        <Col span={24}>
          <Space direction="vertical">
            <Typography.Text>
              {t('frontend:qualification.switchReasonQuestion')}
            </Typography.Text>
            <Typography.Text type="secondary">
              {t('frontend:qualification.multiSelectHint')}
            </Typography.Text>
          </Space>
        </Col>

        <Col span={24}>
          <ConfigProvider
            theme={{
              components: {
                Button: {
                  controlHeight: token.controlHeightLG,
                },
              },
            }}
          >
            <Form.Item
              name={['switchReason']}
              style={{ marginTop: token.marginXS }}
              rules={[
                {
                  required: true,
                  message: t('frontend:qualification.selectRequired'),
                },
              ]}
            >
              <ButtonSelect
                multiple
                span={24}
                miscSelection={t('frontend:qualification.switchReasonNone')}
                items={[
                  {
                    key: 'RepetitiveManualWork',
                    label: (
                      <>{t('frontend:qualification.switchReasonRepetitiveWork')}</>
                    ),
                  },
                  {
                    key: 'HighErrorRate',
                    label: (
                      <>{t('frontend:qualification.switchReasonHighErrorRate')}</>
                    ),
                  },
                  {
                    key: 'TooExpensive',
                    label: (
                      <>{t('frontend:qualification.switchReasonTooExpensive')}</>
                    ),
                  },
                  {
                    key: 'GotCancelled',
                    label: (
                      <>{t('frontend:qualification.switchReasonGotCancelled')}</>
                    ),
                  },
                ]}
              />
            </Form.Item>
          </ConfigProvider>
        </Col>

        <Col
          span={24}
          style={{ display: 'flex', justifyContent: 'space-between' }}
        >
          <Button size="large" onClick={previous}>
            <ArrowLeftOutlined /> {t('frontend:common.back')}
          </Button>
          <Button type="primary" size="large" htmlType="submit">
            {t('frontend:qualification.next')} <ArrowRightOutlined />
          </Button>
        </Col>
      </Row>
    </Form>
  )
}

function StepFirstMonth({ next, previous, data }) {
  const { t } = useTranslation()
  const { token } = theme.useToken()

  const [form] = Form.useForm()

  function onFinish(values) {
    const finalValues = {
      switchDate: values.switchDate,
      firstMonth: values.firstMonth ?? null,
      lastStep: 'firstMonth',
    }
    next(finalValues)
  }

  const months = {}
  let i = 2
  while (i <= 14) {
    const m = dayjs().add(i++, 'month')
    if (!months[m.year()]) {
      months[m.year()] = []
    }
    months[m.year()].push(m)
  }

  const switchDate = Form.useWatch(['switchDate'], { form, preserve: true })

  return (
    <Form
      layout="vertical"
      colon={false}
      requiredMark={false}
      form={form}
      onFinish={onFinish}
      initialValues={data}
    >
      <Row gutter={gutter}>
        <Col span={24}>
          <Typography.Title>
            {t('frontend:qualification.heading')}
          </Typography.Title>
        </Col>
        <Col span={24}>
          <Typography.Text>
            {t('frontend:qualification.firstMonthQuestion')}
          </Typography.Text>
        </Col>

        <Col span={24}>
          <ConfigProvider
            theme={{
              components: {
                Button: {
                  controlHeight: token.controlHeightLG,
                },
              },
            }}
          >
            <Form.Item
              name={['switchDate']}
              style={{ marginTop: token.marginXL }}
              rules={[
                {
                  required: true,
                  message: t('frontend:qualification.selectRequired'),
                },
              ]}
            >
              <ButtonSelect
                items={[
                  {
                    key: 'ASAP',
                    label: <>{t('frontend:qualification.switchDateAsap')}</>,
                  },
                  {
                    key: 'AtTurnOfYear',
                    label: (
                      <>{t('frontend:qualification.switchDateTurnOfYear')}</>
                    ),
                  },
                  {
                    key: 'Undecided',
                    label: (
                      <>{t('frontend:qualification.switchDateUndecided')}</>
                    ),
                  },
                  {
                    key: 'SpecificMonth',
                    label: (
                      <>{t('frontend:qualification.switchDateSpecificMonth')}</>
                    ),
                  },
                ]}
                span={24}
              />
            </Form.Item>
          </ConfigProvider>
        </Col>

        {switchDate === 'SpecificMonth' && (
          <Col span={24}>
            <Form.Item
              name={['firstMonth']}
              style={{ marginTop: token.marginXS }}
              label={t('frontend:qualification.firstMonthLabel')}
              labelAlign="left"
              rules={[
                {
                  required: true,
                  message: t('frontend:qualification.firstMonthRequired'),
                },
              ]}
            >
              <Select size="large">
                {Object.entries(months).map(([year, months]) => (
                  <Select.OptGroup label={year} key={year}>
                    {months.map((m) => (
                      <Select.Option
                        key={m.format('YYYY-MM')}
                        value={m.format('YYYY-MM')}
                      >
                        {m.format('MMMM YYYY')}
                      </Select.Option>
                    ))}
                  </Select.OptGroup>
                ))}
              </Select>
            </Form.Item>
          </Col>
        )}

        <Col
          span={24}
          style={{ display: 'flex', justifyContent: 'space-between' }}
        >
          <Button size="large" onClick={previous}>
            <ArrowLeftOutlined /> {t('frontend:common.back')}
          </Button>
          <Button type="primary" size="large" htmlType="submit">
            {t('frontend:qualification.next')} <ArrowRightOutlined />
          </Button>
        </Col>
      </Row>
    </Form>
  )
}

function StepContact({
  next,
  previous,
  data,
  showCalendly = false,
  showInPersonCalendly = false,
}) {
  const { t } = useTranslation()
  const { token } = theme.useToken()

  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)

  async function onFinish(values) {
    setLoading(true)
    try {
      await next({
        ...values,
        response: showCalendly ? 'calendly' : 'callback',
        lastStep: 'contact',
      })

      if (showCalendly) {
        if (values.meetingType === 'inPerson') {
          window.location.href = `https://calendly.com/cleverlohn/ole-in-person?name=${encodeURIComponent(
            values.contactName
          )} - ${values.companyName}&email=${encodeURIComponent(values.email)}`
        } else {
          window.location.href = `https://calendly.com/cleverlohn/30min?name=${encodeURIComponent(
            values.contactName
          )} - ${values.companyName}&email=${encodeURIComponent(values.email)}`
        }
      }
    } catch (e) {
      errorResponse(e)
      setLoading(false)
    }
  }

  return (
    <Form
      layout="vertical"
      colon={false}
      requiredMark={false}
      form={form}
      onFinish={onFinish}
      initialValues={data}
    >
      <Row gutter={gutter}>
        <Col span={24}>
          <Typography.Title style={{ marginBottom: 0 }}>
            {t('frontend:qualification.heading')}
          </Typography.Title>
        </Col>
        <Col span={24}>
          <Typography.Text>
            {t('frontend:qualification.contactIntro')}
          </Typography.Text>
        </Col>
        <Col span={24}>
          <Typography.Title level={3} style={{ marginBottom: 0 }}>
            {t('frontend:qualification.contactReachQuestion')}
          </Typography.Title>
        </Col>
        <Col span={24}>
          <Row gutter={formGutter}>
            <Col span={24}>
              <Form.Item
                name={['companyName']}
                style={{ marginTop: token.marginXS }}
                label={t('frontend:qualification.companyName')}
                rules={[
                  {
                    required: true,
                    message: t('frontend:qualification.companyNameRequired'),
                  },
                  {
                    max: 100,
                    message: t('frontend:qualification.max100'),
                  },
                ]}
              >
                <Input
                  size="large"
                  placeholder={t('frontend:qualification.companyNamePlaceholder')}
                />
              </Form.Item>
              <Form.Item
                name={['contactName']}
                style={{ marginTop: token.marginXS }}
                label={t('frontend:qualification.contactName')}
                rules={[
                  {
                    required: true,
                    message: t('frontend:qualification.contactNameRequired'),
                  },
                  {
                    max: 100,
                    message: t('frontend:qualification.max100'),
                  },
                ]}
              >
                <Input
                  size="large"
                  placeholder={t('frontend:qualification.contactNamePlaceholder')}
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                label={t('frontend:qualification.phoneNumber')}
                name={['phoneNumber']}
                style={{ marginTop: token.marginXS }}
                rules={[
                  {
                    required: true,
                    message: t('frontend:qualification.phoneNumberRequired'),
                  },
                  {
                    pattern: /^[+0-9\s]{8,}$/,
                    message: t('frontend:qualification.phoneNumberInvalid'),
                  },
                  {
                    max: 30,
                    message: t('frontend:qualification.max30'),
                  },
                ]}
              >
                <Input
                  size="large"
                  placeholder={t('frontend:qualification.phoneNumberPlaceholder')}
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                label={t('frontend:qualification.email')}
                name={['email']}
                style={{ marginTop: token.marginXS }}
                rules={[
                  {
                    required: true,
                    message: t('frontend:qualification.emailRequired'),
                  },
                  {
                    type: 'email',
                    message: t('frontend:qualification.emailInvalid'),
                  },
                  {
                    max: 100,
                    message: t('frontend:qualification.max100'),
                  },
                ]}
              >
                <Input
                  placeholder={t('frontend:qualification.emailPlaceholder')}
                  size="large"
                />
              </Form.Item>
            </Col>
            {showInPersonCalendly && (
              <Col span={24}>
                <Form.Item
                  label={t('frontend:qualification.meetingTypeQuestion')}
                  name={['meetingType']}
                  style={{ marginTop: token.marginXS }}
                  rules={[
                    {
                      required: true,
                      message: t('frontend:qualification.selectRequired'),
                    },
                  ]}
                >
                  <ButtonSelect
                    buttonStyle={{
                      paddingTop: token.paddingMD,
                      paddingBottom: token.paddingMD,
                      borderRadius: token.borderRadiusLG,
                    }}
                    items={[
                      {
                        key: 'online',
                        label: <>{t('frontend:qualification.meetingTypeOnline')}</>,
                      },
                      {
                        key: 'inPerson',
                        label: (
                          <>{t('frontend:qualification.meetingTypeInPerson')}</>
                        ),
                      },
                    ]}
                  />
                </Form.Item>
              </Col>
            )}
          </Row>
        </Col>
        <Col
          span={24}
          style={{ display: 'flex', justifyContent: 'space-between' }}
        >
          <Button size="large" onClick={previous}>
            <ArrowLeftOutlined /> {t('frontend:common.back')}
          </Button>
          <Button
            type="primary"
            size="large"
            htmlType="submit"
            style={{ marginLeft: token.marginXS }}
            loading={loading}
            // icon={showCalendly ? <CalendarOutlined /> : <SendOutlined />}
          >
            {t('frontend:qualification.next')} <ArrowRightOutlined />
          </Button>
        </Col>
      </Row>
    </Form>
  )
}

function StepEnd({ data, forwarding = false }) {
  const { t } = useTranslation()
  const { token } = theme.useToken()
  return (
    <Row gutter={gutter}>
      <Col
        span={24}
        style={{ textAlign: 'center', marginBottom: token.marginMD }}
      >
        <span style={{ fontSize: 100 }}>🎉</span>
      </Col>
      <Col span={24}>
        <Typography.Title style={{ textAlign: 'center' }}>
          {t('frontend:qualification.endTitle')}
        </Typography.Title>
      </Col>
      <Col span={24} style={{ textAlign: 'center' }}>
        <Typography.Paragraph>
          {forwarding ? (
            <>{t('frontend:qualification.endForwarding')}</>
          ) : (
            <Trans
              i18nKey="frontend:qualification.endEmailInfo"
              components={{ strong: <Typography.Text strong /> }}
              values={{ email: data.email }}
            />
          )}
        </Typography.Paragraph>
      </Col>
    </Row>
  )
}
